/* =============================================================
   OrderModal — United Dream Mongolia
   4-step visa order wizard:
   Step 1: Country + Visa type
   Step 2: Person count (adults + children) + auto price
   Step 3: Contact info (name, phone, email optional)
   Step 4: Khan Bank payment instructions + Terms checkbox
   ============================================================= */
import { motion, AnimatePresence } from "framer-motion";
import { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  X, ChevronRight, ChevronLeft, Users, Phone, Mail,
  CheckCircle2, Copy, Check, AlertCircle, Minus, Plus,
  CreditCard, FileText, Globe, ChevronDown
} from "lucide-react";
import { SERVICE_TERMS_BY_VISA, GENERAL_TERMS } from "@/lib/serviceTerms";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { VISA_PRICING, calcTier, getCurrentPrice, getFamilyPrice, getTimeLeft, type PricingTier } from "@/hooks/useUrgencyPricing";

// ── Live countdown hook ──────────────────────────────────────
function useCountdown() {
  const [timeLeft, setTimeLeft] = useState(() => getTimeLeft());
  useEffect(() => {
    const id = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(id);
  }, []);
  return timeLeft;
}

// ── Countdown Banner component ──────────────────────────────
// dark=true → dark header background, dark=false → light card background
function CountdownBanner({ dark = false }: { dark?: boolean }) {
  const countdown = useCountdown();
  const tier = calcTier();
  if (countdown.expired) return null;
  const isWomensDay = tier === "womensday";
  return (
    <div className={`mb-3 rounded-xl px-3 py-2.5 ${
      dark
        ? isWomensDay ? "bg-pink-600/30 border border-pink-400/40" : "bg-white/10 border border-white/20"
        : isWomensDay ? "bg-gradient-to-r from-pink-50 to-rose-50 border border-pink-300" : "bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200"
    }`}>
      <div className={`text-center text-[11px] font-semibold mb-2 ${
        dark
          ? isWomensDay ? "text-pink-300" : "text-amber-300"
          : isWomensDay ? "text-pink-700" : "text-amber-700"
      }`}>
        {isWomensDay ? "💐 3-р сарын 8 — БҮГД 10% ХЯМДРАЛ! Өнөөдөр шөнийн 12:00 хүртэл:" : "🔥 Хямдралтай үнэ дуусахад:"}
      </div>
      <div className="flex justify-center gap-2">
        {[
          { val: countdown.days, label: "өдөр" },
          { val: countdown.hours, label: "цаг" },
          { val: countdown.minutes, label: "мин" },
          { val: countdown.seconds, label: "сек" },
        ].map(({ val, label }) => (
          <div key={label} className="flex flex-col items-center">
            <span className={`font-bold text-sm rounded-lg w-10 h-10 flex items-center justify-center tabular-nums ${
              dark ? "bg-amber-400/90 text-[#0A1628]" : "bg-amber-600 text-white"
            }`}>
              {String(val).padStart(2, "0")}
            </span>
            <span className={`text-[9px] mt-0.5 font-medium ${
              dark ? "text-amber-300" : "text-amber-600"
            }`}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Types ────────────────────────────────────────────────────
interface OrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Pre-select a visa on open */
  preselectedVisaKey?: string;
  /** 'apply' = biднээр мэдүүлэх (20% commission), 'self' = өөрөө мэдүүлэх (50% commission) */
  orderSource?: "apply" | "self";
}

// Country/visa grouping for Step 1
const VISA_GROUPS = [
  {
    country: "usa",
    flag: "🇺🇸",
    label: "АНУ",
    visas: ["usa-travel", "usa-study", "usa-self-bundle"],
  },
  {
    country: "australia",
    flag: "🇦🇺",
    label: "Австрали",
    visas: ["australia-travel", "australia-study", "australia-self-bundle"],
  },
  {
    country: "canada",
    flag: "🇨🇦",
    label: "Канад",
    visas: ["canada-travel", "canada-self-bundle"],
  },
  {
    country: "schengen",
    flag: "🇪🇺",
    label: "Шенген",
    visas: ["schengen-travel", "schengen-self-bundle"],
  },
  {
    country: "ireland",
    flag: "🇮🇪",
    label: "Ирланд",
    visas: ["ireland-study", "ireland-self-bundle"],
  },
  {
    country: "czech",
    flag: "🇨🇿",
    label: "Чех",
    visas: ["czech-work"],
  },
  {
    country: "chicago",
    flag: "🏙️",
    label: "Чикаго аялал",
    visas: ["chicago-tour"],
    highlight: true, // special tour package
  },
];

const BANK_ACCOUNT = "5175057440";
const BANK_IBAN = "67000500";
const BANK_NAME = "Монголиан Юнайтед Дрийм Консалтинг ХХК";
const BANK_BRANCH = "Хаан Банк";

// ── Price calculation ─────────────────────────────────────────
function calcTotal(
  visaKey: string,
  adultCount: number,
  childCount: number,
  tier: PricingTier
): { total: string; breakdown: string; currency: string } {
  const visa = VISA_PRICING.find((v) => v.visaKey === visaKey);
  if (!visa) return { total: "—", breakdown: "", currency: "MNT" };

  const basePrice = getCurrentPrice(visa, tier);
  const familyPrice = getFamilyPrice(visa, tier);

  // Parse numeric value
  const parseNum = (s: string) =>
    parseFloat(s.replace(/[^0-9.]/g, "").replace(/,/g, ""));

  const baseNum = parseNum(basePrice);
  const familyNum = familyPrice ? parseNum(familyPrice) : baseNum * 0.9;

  // USD/EUR study visas: no child price, no family discount
  if (visa.currency !== "MNT") {
    const total = baseNum * adultCount;
    const fmt = visa.currency === "USD"
      ? `$${total.toLocaleString("en-US")}`
      : `€${total.toLocaleString("en-US")}`;
    return {
      total: fmt,
      breakdown: adultCount > 1 ? `${adultCount} хүн × ${basePrice}` : basePrice,
      currency: visa.currency,
    };
  }

  // MNT visas: first adult full price, additional adults 10% off, children ₮100,000
  const firstAdult = baseNum;
  const extraAdults = Math.max(0, adultCount - 1) * familyNum;
  const childTotal = childCount * 100_000;
  const total = firstAdult + extraAdults + childTotal;

  const parts: string[] = [];
  parts.push(`1 насанд хүрсэн: ${baseNum.toLocaleString()}₮`);
  if (adultCount > 1) parts.push(`${adultCount - 1} нэмэлт насанд хүрсэн: ${(extraAdults).toLocaleString()}₮ (10% хөнгөлөлт)`);
  if (childCount > 0) parts.push(`${childCount} хүүхэд: ${childTotal.toLocaleString()}₮`);

  return {
    total: `${total.toLocaleString()}₮`,
    breakdown: parts.join(" + "),
    currency: "MNT",
  };
}

// ── Counter component ─────────────────────────────────────────
function Counter({
  value,
  min,
  max,
  onChange,
  label,
  sublabel,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
  label: string;
  sublabel?: string;
}) {
  return (
    <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50">
      <div>
        <div className="font-semibold text-[#0A1628] text-sm">{label}</div>
        {sublabel && <div className="text-slate-400 text-xs mt-0.5">{sublabel}</div>}
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => onChange(Math.max(min, value - 1))}
          disabled={value <= min}
          className="w-8 h-8 rounded-full border border-slate-300 flex items-center justify-center text-slate-600 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
        <span className="w-8 text-center font-bold text-[#0A1628] text-lg tabular-nums">{value}</span>
        <button
          type="button"
          onClick={() => onChange(Math.min(max, value + 1))}
          disabled={value >= max}
          className="w-8 h-8 rounded-full bg-[#1E6FD9] flex items-center justify-center text-white hover:bg-[#1a5fc4] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// ── CopyButton ────────────────────────────────────────────────
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="ml-2 p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-[#1E6FD9] transition-all"
      title="Хуулах"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
}

// ── Main Component ────────────────────────────────────────────
export default function OrderModal({ isOpen, onClose, preselectedVisaKey, orderSource = "apply" }: OrderModalProps) {
  const tier = useMemo(() => calcTier(), []);

  const [step, setStep] = useState(1);
  const [selectedVisaKey, setSelectedVisaKey] = useState(preselectedVisaKey ?? "");
  const [adultCount, setAdultCount] = useState(1);
  const [childCount, setChildCount] = useState(0);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Coupon state
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; discountPercent: number } | null>(null);
  const [couponError, setCouponError] = useState("");

  const submitOrder = trpc.orders.submit.useMutation();
  const validateCoupon = trpc.coupons.validate.useQuery(
    { code: couponInput.toUpperCase().trim() },
    { enabled: false }
  );

  const selectedVisa = VISA_PRICING.find((v) => v.visaKey === selectedVisaKey);
  const { total, breakdown, currency } = useMemo(
    () => calcTotal(selectedVisaKey, adultCount, childCount, tier),
    [selectedVisaKey, adultCount, childCount, tier]
  );

  // Calculate discounted total when coupon applied
  const discountedTotal = useMemo(() => {
    if (!appliedCoupon || currency !== "MNT") return null;
    const raw = parseInt(total.replace(/[^\d]/g, ""), 10);
    if (isNaN(raw)) return null;
    const discounted = Math.floor(raw * (1 - appliedCoupon.discountPercent / 100));
    return `${discounted.toLocaleString()}₮`;
  }, [appliedCoupon, total, currency]);

  // Reset on open
  const handleOpen = () => {
    setStep(preselectedVisaKey ? 2 : 1);
    setSelectedVisaKey(preselectedVisaKey ?? "");
    setAdultCount(1);
    setChildCount(0);
    setName("");
    setPhone("");
    setEmail("");
    setTermsAccepted(false);
    setSubmitted(false);
    setCouponInput("");
    setAppliedCoupon(null);
    setCouponError("");
  };

  // Track when modal opens
  const [prevOpen, setPrevOpen] = useState(false);
  if (isOpen && !prevOpen) {
    setPrevOpen(true);
    handleOpen();
  }
  if (!isOpen && prevOpen) setPrevOpen(false);

  const canProceedStep1 = !!selectedVisaKey;
  const canProceedStep2 = adultCount >= 1;
  const canProceedStep3 = name.trim().length >= 2 && phone.trim().length >= 8 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit = canProceedStep3 && termsAccepted;

  const handleSubmit = async () => {
    if (!canSubmit || !selectedVisa) return;
    try {
      await submitOrder.mutateAsync({
        country: VISA_GROUPS.find((g) => g.visas.includes(selectedVisaKey))?.country ?? selectedVisaKey,
        visaKey: selectedVisaKey,
        visaName: selectedVisa.visaName,
        adultCount,
        childCount,
        totalPrice: total,
        currency,
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        termsAccepted: "yes",
        couponCode: appliedCoupon?.code,
        discountPercent: appliedCoupon?.discountPercent,
        discountedPrice: discountedTotal ?? undefined,
        orderSource,
      });
      setSubmitted(true);
    } catch {
      toast.error("Захиалга илгээхэд алдаа гарлаа. Дахин оролдоно уу.");
    }
  };

  const STEP_LABELS = ["Виз сонгох", "Хүний тоо", "Холбоо барих", "Төлбөр & Нөхцөл"];

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <motion.div
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 60 }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0A1628] to-[#1a2d4f] px-6 py-5 flex-shrink-0">
          {/* Countdown banner — always visible on all steps */}
          {(tier === "sale" || tier === "womensday") && !submitted && <CountdownBanner dark />}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-white font-bold text-lg">Виз мэдүүлэх</h2>
              <p className="text-white/50 text-xs mt-0.5">United Dream Mongolia</p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white/70 hover:text-white transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Step indicators */}
          {!submitted && (
            <div className="flex items-center gap-1">
              {STEP_LABELS.map((label, i) => {
                const s = i + 1;
                const active = s === step;
                const done = s < step;
                return (
                  <div key={s} className="flex items-center gap-1 flex-1">
                    <div className="flex flex-col items-center gap-1 flex-1">
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                          done ? "bg-green-400 text-white" : active ? "bg-[#1E6FD9] text-white" : "bg-white/10 text-white/40"
                        }`}
                      >
                        {done ? <Check className="w-3 h-3" /> : s}
                      </div>
                      <span className={`text-[10px] text-center leading-tight ${active ? "text-white" : "text-white/40"}`}>
                        {label}
                      </span>
                    </div>
                    {i < STEP_LABELS.length - 1 && (
                      <div className={`h-px flex-1 mb-4 ${done ? "bg-green-400" : "bg-white/15"}`} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {submitted && (
            <SuccessView name={name} phone={phone} visaName={selectedVisa?.visaName ?? ""} total={total} onClose={onClose} appliedCoupon={appliedCoupon} discountedTotal={discountedTotal} />
          )}
          {!submitted && step === 1 && (
            <Step1View selectedVisaKey={selectedVisaKey} onSelect={setSelectedVisaKey} tier={tier} />
          )}
          {!submitted && step === 2 && (
            <Step2View
              selectedVisa={selectedVisa}
              adultCount={adultCount}
              childCount={childCount}
              onAdultChange={setAdultCount}
              onChildChange={setChildCount}
              total={total}
              breakdown={breakdown}
              tier={tier}
              couponInput={couponInput}
              onCouponInputChange={setCouponInput}
              appliedCoupon={appliedCoupon}
              onApplyCoupon={async () => {
                if (!couponInput.trim()) return;
                setCouponError("");
                const result = await validateCoupon.refetch();
                if (result.data?.valid) {
                  setAppliedCoupon({ code: couponInput.toUpperCase().trim(), discountPercent: result.data.discountPercent });
                  setCouponError("");
                  toast.success(result.data.message);
                } else {
                  setAppliedCoupon(null);
                  setCouponError(result.data?.message ?? "Купон код олдсонгүй.");
                }
              }}
              onRemoveCoupon={() => { setAppliedCoupon(null); setCouponInput(""); setCouponError(""); }}
              couponError={couponError}
              discountedTotal={discountedTotal}
              isCouponLoading={validateCoupon.isFetching}
            />
          )}
          {!submitted && step === 3 && (
            <Step3View
              name={name}
              phone={phone}
              email={email}
              onNameChange={setName}
              onPhoneChange={setPhone}
              onEmailChange={setEmail}
            />
          )}
          {!submitted && step === 4 && (
            <Step4View
              phone={phone}
              total={total}
              visaName={selectedVisa?.visaName ?? ""}
              visaKey={selectedVisaKey}
              termsAccepted={termsAccepted}
              onTermsChange={setTermsAccepted}
              appliedCoupon={appliedCoupon}
              discountedTotal={discountedTotal}
            />
          )}
        </div>

        {/* Footer nav */}
        {!submitted && (
          <div className="flex-shrink-0 px-6 py-4 border-t border-slate-100 bg-white flex gap-3">
            {step > 1 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="flex items-center gap-1.5 px-4 py-3 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-medium transition-all"
              >
                <ChevronLeft className="w-4 h-4" />
                Буцах
              </button>
            )}
            {step < 4 ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                disabled={
                  (step === 1 && !canProceedStep1) ||
                  (step === 2 && !canProceedStep2) ||
                  (step === 3 && !canProceedStep3)
                }
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#1E6FD9] text-white font-semibold text-sm hover:bg-[#1a5fc4] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-md shadow-[#1E6FD9]/30"
              >
                Үргэлжлүүлэх
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={!canSubmit || submitOrder.isPending}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#1E6FD9] text-white font-semibold text-sm hover:bg-[#1a5fc4] disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-md shadow-[#1E6FD9]/30"
              >
                {submitOrder.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Илгээж байна...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4" />
                    Виз мэдүүлэх баталгаажуулах
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </motion.div>
    </div>,
    document.body
  );
}

// ── Step 1: Visa selection ────────────────────────────────────
function Step1View({
  selectedVisaKey,
  onSelect,
  tier,
}: {
  selectedVisaKey: string;
  onSelect: (key: string) => void;
  tier: PricingTier;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="p-6 space-y-4"
    >
      <div>
        <h3 className="font-bold text-[#0A1628] text-base mb-1">Очих улс болон визийн төрлийг сонгоно уу</h3>
        <p className="text-slate-400 text-xs">Нийт 6 улсын 8 төрлийн визийн үйлчилгээ + хөтөлбөрт аялал</p>
      </div>

      <div className="space-y-3">
        {VISA_GROUPS.map((group) => {
          const isHighlight = (group as any).highlight;
          const groupVisas = VISA_PRICING.filter((v) => group.visas.includes(v.visaKey));
          return (
            <div key={group.country} className={isHighlight ? "border border-amber-300 rounded-xl p-3 bg-gradient-to-r from-amber-50 to-orange-50" : ""}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg">{group.flag}</span>
                <span className={`font-semibold text-sm ${isHighlight ? "text-amber-700" : "text-[#0A1628]"}`}>{group.label}</span>
                {isHighlight && <span className="text-[10px] bg-amber-100 text-amber-600 font-bold rounded-full px-2 py-0.5">ХӨТӨЛБӨРТ АЯЛАЛ</span>}
              </div>
              <div className="space-y-2 pl-7">
                {groupVisas.map((visa) => {
                  const price = getCurrentPrice(visa, tier);
                  const isSelected = selectedVisaKey === visa.visaKey;
                  return (
                    <button
                      key={visa.visaKey}
                      type="button"
                      onClick={() => onSelect(visa.visaKey)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border text-left transition-all ${
                        isSelected
                          ? "border-[#1E6FD9] bg-[#1E6FD9]/5 ring-1 ring-[#1E6FD9]/30"
                          : "border-slate-200 hover:border-[#1E6FD9]/40 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <div
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                            isSelected ? "border-[#1E6FD9] bg-[#1E6FD9]" : "border-slate-300"
                          }`}
                        >
                          {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                        </div>
                        <span className="text-[#0A1628] text-sm font-medium">{visa.visaName}</span>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        {tier === "womensday" && visa.currency === "MNT" ? (
                          <>
                            {/* Womensday: show original sale price struck through, then discounted price */}
                            <div className="flex items-center justify-end gap-1.5">
                              <span className="text-[11px] text-slate-400 line-through">{visa.salePrice}</span>
                              <span className="text-[9px] bg-pink-100 text-pink-600 font-bold rounded px-1 py-0.5">-10%</span>
                            </div>
                            <div className="text-sm font-bold text-pink-600">{price}</div>
                          </>
                        ) : (
                          <>
                            <div className={`text-sm font-bold ${
                              tier === "sale" ? "text-[#1E6FD9]" : "text-[#0A1628]"
                            }`}>
                              {price}
                            </div>
                            {tier === "sale" && (
                              <div className="text-[10px] text-slate-400 line-through">{visa.normalPrice}</div>
                            )}
                          </>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

// ── Step 2: Person count ──────────────────────────────────────
function Step2View({
  selectedVisa,
  adultCount,
  childCount,
  onAdultChange,
  onChildChange,
  total,
  breakdown,
  tier,
  couponInput,
  onCouponInputChange,
  appliedCoupon,
  onApplyCoupon,
  onRemoveCoupon,
  couponError,
  discountedTotal,
  isCouponLoading,
}: {
  selectedVisa: (typeof VISA_PRICING)[0] | undefined;
  adultCount: number;
  childCount: number;
  onAdultChange: (v: number) => void;
  onChildChange: (v: number) => void;
  total: string;
  breakdown: string;
  tier: PricingTier;
  couponInput: string;
  onCouponInputChange: (v: string) => void;
  appliedCoupon: { code: string; discountPercent: number } | null;
  onApplyCoupon: () => void;
  onRemoveCoupon: () => void;
  couponError: string;
  discountedTotal: string | null;
  isCouponLoading: boolean;
}) {
  const isMNT = selectedVisa?.currency === "MNT";

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="p-6 space-y-5"
    >
      <div>
        <h3 className="font-bold text-[#0A1628] text-base mb-1">Хэдэн хүн мэдүүлэх вэ?</h3>
        <p className="text-slate-400 text-xs">Нэмэлт насанд хүрсэн гишүүн бүрт 10% хөнгөлөлт</p>
      </div>

      {/* Selected visa summary */}
      {selectedVisa && (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-[#1E6FD9]/5 border border-[#1E6FD9]/20">
          <Globe className="w-4 h-4 text-[#1E6FD9] flex-shrink-0" />
          <div>
            <div className="text-[#0A1628] text-sm font-medium">{selectedVisa.visaName}</div>
            <div className="text-[#1E6FD9] text-xs font-semibold">{getCurrentPrice(selectedVisa, tier)}</div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <Counter
          value={adultCount}
          min={1}
          max={10}
          onChange={onAdultChange}
          label="Насанд хүрсэн"
          sublabel="18 ба түүнээс дээш"
        />
        {isMNT && (
          <Counter
            value={childCount}
            min={0}
            max={10}
            onChange={onChildChange}
            label="Хүүхэд"
            sublabel="18-аас доош — ₮100,000 тогтмол"
          />
        )}
      </div>

      {/* Price summary */}
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
          <div className="text-xs text-slate-500 font-medium">Үйлчилгээний хөлсний тооцоо</div>
        </div>
        <div className="px-4 py-3 space-y-1.5">
          {breakdown.split(" + ").map((line, i) => (
            <div key={i} className="flex justify-between text-sm">
              <span className="text-slate-500">{line.split(":")[0]}:</span>
              <span className="text-[#0A1628] font-medium">{line.split(":")[1]}</span>
            </div>
          ))}
          <div className="pt-2 border-t border-slate-100">
            {tier === "womensday" && selectedVisa?.currency === "MNT" && (() => {
              // Calculate original total (at sale price) for comparison
              const origTotal = calcTotal(selectedVisa.visaKey, adultCount, childCount, "sale");
              return (
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-slate-400 text-sm">Анхны үнэ:</span>
                  <span className="text-slate-400 text-sm line-through">{origTotal.total}</span>
                </div>
              );
            })()}
              <div className="flex justify-between items-center">
              <span className="font-bold text-[#0A1628]">
                {tier === "womensday" ? (
                  <span className="flex items-center gap-1.5">
                    Үйлчилгээний хөлс
                    <span className="text-[10px] bg-pink-100 text-pink-600 font-bold rounded px-1.5 py-0.5">-10%</span>
                  </span>
                ) : "Үйлчилгээний хөлс:"}
              </span>
              <span className={`font-bold text-lg ${
                tier === "womensday" ? "text-pink-600" : "text-[#1E6FD9]"
              }`}>
                {total}
              </span>
            </div>
          </div>
          {(tier === "sale" || tier === "womensday") && (
            <CountdownBanner />
          )}
        </div>
      </div>

      {/* ── Coupon Code Input ── */}
      {isMNT && (
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
            <div className="text-xs text-slate-500 font-medium">🏷️ Referral / Купон код <span className="text-slate-400 font-normal">(заавал биш)</span></div>
            <div className="text-[10px] text-slate-400 mt-0.5">Агентын referral код буюу бол доор оруулна үү. Байхгүй бол үргэлжлүүлэх боломжтой.</div>
          </div>
          <div className="px-4 py-3">
            {appliedCoupon ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <div>
                      <div className="text-green-800 font-bold text-sm">{appliedCoupon.code} код бүртгэгдлэлээ</div>
                      <div className="text-green-600 text-xs">{appliedCoupon.discountPercent}% хөнгөлөлт баталгаажсан</div>
                    </div>
                  </div>
                  <button
                    onClick={onRemoveCoupon}
                    className="text-slate-400 hover:text-slate-600 text-xs underline ml-2"
                  >
                    Хасах
                  </button>
                </div>
                {discountedTotal && (
                  <div className="space-y-2">
                    {/* Discounted price highlight */}
                    <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
                      <div className="flex items-center justify-between">
                        <span className="text-green-700 text-xs font-semibold">🎉 {appliedCoupon?.discountPercent}% хөнгөлөлттэй үнэ:</span>
                        <span className="text-green-800 font-black text-lg">{discountedTotal}</span>
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <span className="text-green-600 text-xs">Хасагдсан дүн:</span>
                        <span className="text-green-500 text-xs line-through">{total}</span>
                      </div>
                    </div>
                    {/* Payment instruction */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                      <div className="flex items-start gap-2">
                        <span className="text-amber-600 text-base flex-shrink-0">⚠️</span>
                        <div className="text-xs text-amber-800">
                          <span className="font-bold">QPay дээр үндсэн үнээр төлнө үү:</span> {total}<br />
                          <span className="text-amber-700">Төлбөр хийгдсэний дараа <strong>{discountedTotal}</strong> (хөнгөлөлтийн дүн) тань дансанд буцааж шилжүүлнэ.</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={couponInput}
                    onChange={(e) => onCouponInputChange(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && onApplyCoupon()}
                    placeholder="Купон кодоо оруулна үү..."
                    className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E6FD9]/30 focus:border-[#1E6FD9] uppercase placeholder:normal-case"
                  />
                  <button
                    onClick={onApplyCoupon}
                    disabled={!couponInput.trim() || isCouponLoading}
                    className="px-4 py-2 bg-[#1E6FD9] text-white text-sm font-semibold rounded-lg hover:bg-[#1a5fc4] disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1.5"
                  >
                    {isCouponLoading ? (
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : "Хэрэглэх"}
                  </button>
                </div>
                {couponError && (
                  <p className="text-red-500 text-xs flex items-center gap-1">
                    <span>⚠️</span> {couponError}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Trust Checklist: Fear Reducer ── */}
      <div className="rounded-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0A1628] to-[#1a2d4f] px-4 py-3">
          <div className="text-white font-bold text-sm">✅ Та юу бэлдэх вэ?</div>
          <div className="text-white/60 text-[11px] mt-0.5">Бусад бүгдийг United Dream хийнэ</div>
        </div>
        {/* User documents */}
        <div className="px-4 py-3 border-b border-slate-100">
          <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-2">Та зөвхөн энэ л хэрэгтэй</div>
          <div className="space-y-2">
            {[
              { icon: "🛂", text: "Хүчинтэй паспорт" },
              { icon: "📸", text: "Цээж зураг (3.5×4.5 см)" },
              { icon: "🏦", text: "Банкны данс (үлдэгдэл хамаагүй — бид тусална)" },
              { icon: "📋", text: "Иргэний үнэмлэх" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <span className="text-base">{item.icon}</span>
                <span className="text-sm text-[#0A1628]">{item.text}</span>
                <CheckCircle2 className="w-4 h-4 text-green-500 ml-auto flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
        {/* United Dream handles */}
        <div className="px-4 py-3 border-b border-slate-100 bg-blue-50/40">
          <div className="text-[11px] font-bold text-[#1E6FD9] uppercase tracking-wide mb-2">United Dream бүгдийг хийнэ</div>
          <div className="space-y-2">
            {[
              { icon: "📝", text: "Элчингийн анкет бөглөх" },
              { icon: "🗺️", text: "Аялалын төлөвлөгөө боловсруулах" },
              { icon: "📄", text: "Дэмжих захидал бичих" },
              { icon: "🌐", text: "Баримт бичиг орчуулах" },
              { icon: "📅", text: "Ярилцлагын цаг захиалах" },
              { icon: "🎯", text: "Ярилцлагад бэлтгэх" },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <span className="text-base">{item.icon}</span>
                <span className="text-sm text-[#0A1628]">{item.text}</span>
                <CheckCircle2 className="w-4 h-4 text-[#1E6FD9] ml-auto flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
        {/* Fear-reducing reassurance */}
        <div className="px-4 py-3 bg-green-50">
          <div className="text-[11px] font-bold text-green-700 uppercase tracking-wide mb-2">💚 Санаа зовох хэрэггүй</div>
          <div className="space-y-2">
            {[
              "Ажилгүй байсан ч виз гарах боломжтой — манай үйлчлүүлэгчдийн олонх нь ажилгүй байсан",
              "Банкны үлдэгдэл бага байсан ч болно — бага үлдэгдэлтэй хүмүүс амжилттай виз авсан",
              "Анх удаагаа мэдүүлж байгаа ч болно — бид алхам алхмаар тайлбарлана",
              "Өмнө нь татгалзсан байсан ч болно — бид тусгай стратеги боловсруулна",
            ].map((msg, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-green-500 text-sm mt-0.5 flex-shrink-0">✓</span>
                <span className="text-xs text-green-800 leading-relaxed">{msg}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Visa government fee notice */}
      {selectedVisa?.visaFeeNote && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 overflow-hidden">
          <div className="px-4 py-3 space-y-2">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-amber-800 font-semibold text-xs mb-1">Визний хураамж — тусдаа төлнө</div>
                <div className="text-amber-700 text-sm font-medium">{selectedVisa.visaFeeNote}</div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-amber-600 text-xs">{selectedVisa.visaFeePaidWhen}</span>
                  <span className="text-amber-800 font-bold text-sm">{selectedVisa.visaFeeAmount}</span>
                </div>
              </div>
            </div>
            <p className="text-amber-600 text-[11px] leading-relaxed pl-6">
              Дээрх визний хураамжийг United Dream Mongolia-д биш, харин элчин сайдын яам / засгийн газрын системд шууд төлнө. Манай зөвлөх яг хэзээ, хэрхэн төлөхийг тайлбарлана.
            </p>
          </div>
        </div>
      )}
    </motion.div>
  );
}

// ── Step 3: Contact info ──────────────────────────────────────
function Step3View({
  name,
  phone,
  email,
  onNameChange,
  onPhoneChange,
  onEmailChange,
}: {
  name: string;
  phone: string;
  email: string;
  onNameChange: (v: string) => void;
  onPhoneChange: (v: string) => void;
  onEmailChange: (v: string) => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="p-6 space-y-5"
    >
      <div>
        <h3 className="font-bold text-[#0A1628] text-base mb-1">Холбоо барих мэдээлэл</h3>
        <p className="text-slate-400 text-xs">Манай зөвлөх таны утасны дугаарт холбогдоно</p>
      </div>

      <div className="space-y-4">
        {/* Name */}
        <div>
          <label className="block text-sm font-semibold text-[#0A1628] mb-1.5">
            Нэр <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Users className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={name}
              onChange={(e) => onNameChange(e.target.value)}
              placeholder="Таны нэр"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-[#0A1628] placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E6FD9]/30 focus:border-[#1E6FD9] transition-all"
            />
          </div>
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-semibold text-[#0A1628] mb-1.5">
            Утасны дугаар <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="tel"
              value={phone}
              onChange={(e) => onPhoneChange(e.target.value)}
              placeholder="+976 XXXX XXXX"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-[#0A1628] placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E6FD9]/30 focus:border-[#1E6FD9] transition-all"
            />
          </div>
          <p className="text-xs text-slate-400 mt-1">Гүйлгээний утганд энэ дугаарыг бичнэ</p>
        </div>

        {/* Email (required) */}
        <div>
          <label className="block text-sm font-semibold text-[#0A1628] mb-1.5">
            Имэйл хаяг <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              placeholder="example@gmail.com"
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-[#0A1628] placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E6FD9]/30 focus:border-[#1E6FD9] transition-all"
            />
          </div>
          <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Захиалгын баталгаажуулалт болон визийн материалын жагсаалт имэйлд илгээнэ
          </p>
        </div>
      </div>

      <div className="p-3 rounded-xl bg-green-50 border border-green-200 flex items-start gap-2.5">
        <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
        <p className="text-green-700 text-xs">
          Таны мэдээлэл нууцлалтай хадгалагдана. Зөвхөн визийн зөвлөгөөний зорилгоор ашиглана.
        </p>
      </div>
    </motion.div>
  );
}

// ── Step 4: Payment + Terms ───────────────────────────────────
function Step4View({
  phone,
  total,
  visaName,
  visaKey,
  termsAccepted,
  onTermsChange,
  appliedCoupon,
  discountedTotal,
}: {
  phone: string;
  total: string;
  visaName: string;
  visaKey: string;
  termsAccepted: boolean;
  onTermsChange: (v: boolean) => void;
  appliedCoupon: { code: string; discountPercent: number } | null;
  discountedTotal: string | null;
}) {
  const [termsOpen, setTermsOpen] = useState(false);
  const [qpayFaqOpen, setQpayFaqOpen] = useState(false);

  // Look up terms by exact visaKey (e.g. "usa-travel", "usa-study")
  const visaTerms = SERVICE_TERMS_BY_VISA[visaKey] ?? null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="p-6 space-y-5"
    >
      <div>
        <h3 className="font-bold text-[#0A1628] text-base mb-1">Төлбөр шилжүүлэх</h3>
        <p className="text-slate-400 text-xs">Дараах дансанд шилжүүлэн, гүйлгээний утганд утасны дугаараа бичнэ үү</p>
      </div>

      {/* Bank card */}
      <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
        <div className="bg-gradient-to-r from-[#0A1628] to-[#1a3a6e] px-5 py-4">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard className="w-5 h-5 text-[#60a5fa]" />
            <span className="text-white font-bold text-sm">{BANK_BRANCH}</span>
          </div>
          <div className="text-white/60 text-xs mb-1">Байгууллагын нэр</div>
          <div className="text-white font-semibold text-sm mb-3">{BANK_NAME}</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-white/50 text-[10px] mb-0.5">IBAN</div>
              <div className="flex items-center">
                <span className="text-white font-mono font-bold text-sm">{BANK_IBAN}</span>
                <CopyButton text={BANK_IBAN} />
              </div>
            </div>
            <div>
              <div className="text-white/50 text-[10px] mb-0.5">Дансны дугаар</div>
              <div className="flex items-center">
                <span className="text-white font-mono font-bold text-sm">{BANK_ACCOUNT}</span>
                <CopyButton text={BANK_ACCOUNT} />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-amber-50 border-t border-amber-200 px-5 py-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="text-amber-800 font-semibold text-xs mb-0.5">Гүйлгээний утга — ЗААВАЛ</div>
              <div className="flex items-center gap-1">
                <span className="text-amber-700 font-mono font-bold text-sm">{phone || "Таны утасны дугаар"}</span>
                {phone && <CopyButton text={phone} />}
              </div>
              <p className="text-amber-600 text-[10px] mt-0.5">Гүйлгээний утганд утасны дугаараа бичнэ үү</p>
            </div>
          </div>
        </div>

        <div className="bg-white px-5 py-3 border-t border-slate-100">
          <div className="flex justify-between items-center">
            <span className="text-slate-500 text-sm">Шилжүүлэх дүн:</span>
            <span className="font-bold text-[#1E6FD9] text-lg">{total}</span>
          </div>
          {appliedCoupon && discountedTotal && (
            <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <div className="flex items-start gap-1.5">
                <span className="text-amber-600 text-sm flex-shrink-0">💰</span>
                <p className="text-amber-800 text-[11px] leading-relaxed">
                  <strong>{appliedCoupon.discountPercent}% хөнгөлөлтийн дүн ({discountedTotal})</strong> төлбөр хийгдсэний дараа тань дансанд буцааж шилжүүлнэ.
                </p>
              </div>
            </div>
          )}
          <p className="text-slate-400 text-[10px] mt-1">
            Захиалга баталгаажсаны дараа манай зөвлөх 24 цагийн дотор холбогдоно
          </p>
        </div>
      </div>

      {/* QPay FAQ accordion */}
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <button
          type="button"
          onClick={() => setQpayFaqOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-amber-500" />
            <span className="text-sm text-slate-600 font-medium">QPay ажиллахгүй бол яах вэ?</span>
          </div>
          <ChevronDown
            className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
              qpayFaqOpen ? "rotate-180" : ""
            }`}
          />
        </button>
        <AnimatePresence initial={false}>
          {qpayFaqOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="px-4 py-4 border-t border-slate-100 space-y-3">
                <p className="text-xs text-slate-600">QPay ажиллахгүй тохиолдолд дараах аргаар төлбөрөө хийж болно:</p>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="text-base">🏦</span>
                    <div>
                      <p className="text-xs font-semibold text-slate-700">Дансаар шилжүүлэх</p>
                      <p className="text-xs text-slate-500">Хаан Банк · <span className="font-mono">5175057440</span> · Монголиан Юнайтед Дрийм Консалтинг ХХК</p>
                      <p className="text-xs text-amber-600 mt-0.5">⚠️ Гүйлгээний утга: утасны дугаараа бичнэ үү</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-base">📞</span>
                    <div>
                      <p className="text-xs font-semibold text-slate-700">Менежертэй холбогдох</p>
                      <p className="text-xs text-slate-500">Асуудал гарвал манай менежер туслах болно</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Order summary */}
      <div className="p-3 rounded-xl bg-slate-50 border border-slate-200 flex items-start gap-2.5">
        <FileText className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-slate-500">
          <span className="font-semibold text-[#0A1628]">Үйлчилгээний хөлс: </span>
          {visaName} — <span className="text-[#1E6FD9] font-bold">{total}</span>
        </div>
      </div>

      {/* Visa fee reminder */}
      <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-2.5">
        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="text-xs text-amber-700">
          <span className="font-semibold text-amber-800">Визний хураамж — тусдаа төлнө: </span>
          Элчин сайдын яамын визний хураамжийг дээрх дүнд оруулаагүй. Манай зөвлөх яг хэзээ, хэрхэн төлөхийг тайлбарлана.
        </div>
      </div>

      {/* ── Service Terms Accordion ── */}
      <div className="rounded-2xl border border-slate-200 overflow-hidden">
        <button
          type="button"
          onClick={() => setTermsOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#1E6FD9]" />
            <span className="font-semibold text-[#0A1628] text-sm">Үйлчилгээний нөхцөл</span>
          </div>
          <ChevronDown
            className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
              termsOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        <AnimatePresence initial={false}>
          {termsOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="px-4 py-4 space-y-4 border-t border-slate-100">
                {/* Per-visa service inclusions */}
                {visaTerms && (
                  <div>
                    <div className="text-xs font-bold text-[#1E6FD9] uppercase tracking-wide mb-2">
                      {visaTerms.visaType}
                    </div>
                    <ul className="space-y-1.5">
                      {visaTerms.services.map((svc: string, i: number) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-xs text-slate-600">{svc}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* General terms */}
                <div>
                  <div className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                    Ерөнхий нөхцөл
                  </div>
                  <ul className="space-y-2">
                    {GENERAL_TERMS.map((term, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-slate-400 text-xs mt-0.5 flex-shrink-0">{i + 1}.</span>
                        <span className="text-xs text-slate-600 leading-relaxed">{term}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Terms Checkbox ── */}
      <label
        className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
          termsAccepted
            ? "border-[#1E6FD9] bg-[#1E6FD9]/5"
            : "border-slate-200 bg-slate-50 hover:border-[#1E6FD9]/40"
        }`}
      >
        <div
          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
            termsAccepted ? "border-[#1E6FD9] bg-[#1E6FD9]" : "border-slate-300 bg-white"
          }`}
        >
          {termsAccepted && <Check className="w-3 h-3 text-white" />}
        </div>
        <input
          type="checkbox"
          className="sr-only"
          checked={termsAccepted}
          onChange={(e) => onTermsChange(e.target.checked)}
        />
        <span className="text-sm text-[#0A1628] leading-relaxed">
          Үйлчилгээний нөхцөлтэй танилцаж,{" "}
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); setTermsOpen(true); }}
            className="text-[#1E6FD9] underline underline-offset-2 hover:text-[#1a5fc4]"
          >
            нөхцөлийг уншсан
          </button>
          {" "}бөгөөд зөвшөөрч байна.
        </span>
      </label>

      {!termsAccepted && (
        <p className="text-xs text-amber-600 flex items-center gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          Үйлчилгээний нөхцөлийг зөвшөөрсний дараа захиалга баталгаажуулах боломжтой.
        </p>
      )}
    </motion.div>
  );
}

// ── Success view ──────────────────────────────────────────────
function SuccessView({
  name,
  phone,
  visaName,
  total,
  onClose,
  appliedCoupon,
  discountedTotal,
}: {
  name: string;
  phone: string;
  visaName: string;
  total: string;
  onClose: () => void;
  appliedCoupon: { code: string; discountPercent: number } | null;
  discountedTotal: string | null;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="p-8 flex flex-col items-center text-center"
    >
      <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-5">
        <CheckCircle2 className="w-10 h-10 text-green-500" />
      </div>
      <h3 className="font-bold text-[#0A1628] text-xl mb-2">Захиалга амжилттай!</h3>
      <p className="text-slate-500 text-sm mb-6 max-w-xs">
        <strong>{name}</strong>, таны захиалга хүлээн авлаа. Манай зөвлөх <strong>{phone}</strong> дугаарт 24 цагийн дотор холбогдоно.
      </p>

      <div className="w-full p-4 rounded-xl bg-[#1E6FD9]/5 border border-[#1E6FD9]/20 text-left mb-6">
        <div className="text-xs text-slate-500 mb-1">Захиалгын дэлгэрэнгүй</div>
        <div className="font-semibold text-[#0A1628] text-sm">{visaName}</div>
        <div className="text-[#1E6FD9] font-bold text-lg mt-1">{total}</div>
      </div>

      <div className="w-full p-3 rounded-xl bg-amber-50 border border-amber-200 text-left mb-4">
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-700">
            <strong>Санануулга:</strong> Гүйлгээний утганд <strong>{phone}</strong> дугаараа бичиж, дансанд шилжүүлнэ үү. Төлбөр хийгдсэний дараа захиалга баталгаажна.
          </div>
        </div>
      </div>

      {appliedCoupon && discountedTotal && (
        <div className="w-full p-3 rounded-xl bg-green-50 border border-green-200 text-left mb-6">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-green-800">
              <strong>Хөнгөлөлтийн буцаалт:</strong> Тань төлбөр баталгаажсаны дараа <strong>{discountedTotal}</strong> ({appliedCoupon.discountPercent}% хөнгөлөлт) тань дансанд буцааж шилжүүлнэ.
            </div>
          </div>
        </div>
      )}

      <button
        onClick={onClose}
        className="w-full py-3 rounded-xl bg-[#1E6FD9] text-white font-semibold text-sm hover:bg-[#1a5fc4] transition-all"
      >
        Хаах
      </button>
    </motion.div>
  );
}
